import {EngineConfig_Media, MediaPool} from "../../../../Server/DB/engineConfigs/@EngineConfig/@EC_Media.js";
import {FieldLabel} from "../../../@Shared/FieldLabel.js";
import {Clone} from "js-vextensions";
import {observer_mgl} from "mobx-graphlink";
import React, {useState} from "react";
import {Button, CheckBox, Column, Row, RowLR, Spinner, Text, TextInput} from "react-vcomponents";
import {ShowMessageBox} from "react-vmessagebox";
import {InfoButton, TextPlus} from "web-vcore";
import {SpinnerForPercent} from "../../../../Utils/ReactComponents/SpinnerForPercent.js";
import {ControlInput} from "../../../@Shared/ControlInput.js";
import {EngineConfigPanels_SharedProps, ExtendEngineConfigPanelSharedProps} from "../ConfigPanel_Local.js";

export type SharedProps_MediaConfigUI = {enabled?: boolean, config: EngineConfig_Media, ChangeConfig: (mutator: (config: EngineConfig_Media)=>any)=>void};

const GetSharedProps = (props: EngineConfigPanels_SharedProps)=>ExtendEngineConfigPanelSharedProps(props, c=>c.media, (c, v)=>c.media = v);

const splitAt = 230;

export const Media_ConfigUI = observer_mgl((props: {remote?: boolean} & EngineConfigPanels_SharedProps)=>{
	const {enabled} = props;
	const sharedProps = GetSharedProps(props);
	const {config, ChangeConfig} = sharedProps;
	const fp = (fieldName: keyof typeof config)=>`media.${fieldName}`;
	const [selectedPoolIndex, setSelectedPoolIndex] = useState(0);
	const pools = config.mediaPools ?? [];

	return (
		<Column mt={15} style={{background: "hsla(0,0%,100%,.1)", borderRadius: 5, padding: 5}}>
			<Row style={{fontSize: 18}}>
				<CheckBox text="Media" enabled={enabled} value={config.enabled} onChange={val=>ChangeConfig(c=>c.enabled = val)}/>
			</Row>

			{config.enabled &&
			<>
				{/* Hotkey trigger sets */}
				<RowLR mt={5} splitAt={splitAt}>
					<FieldLabel text="Toggle playing:" fieldPath={fp("togglePlaying_triggerSet")}/>
					<ControlInput enabled={enabled} value={config.togglePlaying_triggerSet} onChange={val=>ChangeConfig(c=>c.togglePlaying_triggerSet = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<FieldLabel text="Jump forward:" fieldPath={fp("jumpForward_triggerSet")}/>
					<ControlInput enabled={enabled} value={config.jumpForward_triggerSet} onChange={val=>ChangeConfig(c=>c.jumpForward_triggerSet = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<FieldLabel text="Jump backward:" fieldPath={fp("jumpBackward_triggerSet")}/>
					<ControlInput enabled={enabled} value={config.jumpBackward_triggerSet} onChange={val=>ChangeConfig(c=>c.jumpBackward_triggerSet = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<FieldLabel text="Increase play speed:" fieldPath={fp("increasePlaySpeed_triggerSet")}/>
					<ControlInput enabled={enabled} value={config.increasePlaySpeed_triggerSet} onChange={val=>ChangeConfig(c=>c.increasePlaySpeed_triggerSet = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<FieldLabel text="Decrease play speed:" fieldPath={fp("decreasePlaySpeed_triggerSet")}/>
					<ControlInput enabled={enabled} value={config.decreasePlaySpeed_triggerSet} onChange={val=>ChangeConfig(c=>c.decreasePlaySpeed_triggerSet = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<FieldLabel text="Mark desync caught:" fieldPath={fp("markDesyncCaught_triggerSet")}/>
					<ControlInput enabled={enabled} value={config.markDesyncCaught_triggerSet} onChange={val=>ChangeConfig(c=>c.markDesyncCaught_triggerSet = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<FieldLabel text="Jump seconds:" fieldPath={fp("jumpSeconds")}/>
					<Spinner min={1} step={1} value={config.jumpSeconds} onChange={val=>ChangeConfig(c=>c.jumpSeconds = val)}/>
					<Text ml={3}>s</Text>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<FieldLabel text="Speed step:" fieldPath={fp("speedStep")}/>
					<Spinner min={0.05} step={0.05} value={config.speedStep} onChange={val=>ChangeConfig(c=>c.speedStep = val)}/>
				</RowLR>

				{/* Desync training */}
				<Row mt={10} center>
					<CheckBox style={{fontWeight: "bold", opacity: 0.85}} text="Desync Training:"
						value={config.desync_enabled} onChange={val=>ChangeConfig(c=>c.desync_enabled = val)}/>
					<InfoButton ml={5} text={`
						Adds artificial pan-delay periods during playback. While active the audio pan lags behind the real breath direction.
						Purpose: Letting you practice noticing the mismatch, building the same awareness for detecting desyncs during dreams.
						Note: Enabling this also forces the player's panning-type to "simple". (relevant if global panning-type fallback is something else) 
					`.AsMultiline(0)}/>
				</Row>
				{config.desync_enabled && <>
					<RowLR mt={5} splitAt={splitAt}>
						<FieldLabel text="Start time (min):" fieldPath={fp("desync_startTimeMin")}/>
						<Spinner min={0} step={0.5} value={config.desync_startTimeMin} onChange={val=>ChangeConfig(c=>c.desync_startTimeMin = val)}/>
						<Text ml={3}>min</Text>
					</RowLR>
					<RowLR mt={5} splitAt={splitAt}>
						<FieldLabel text="Start time (max):" fieldPath={fp("desync_startTimeMax")}/>
						<Spinner min={0} step={0.5} value={config.desync_startTimeMax} onChange={val=>ChangeConfig(c=>c.desync_startTimeMax = val)}/>
						<Text ml={3}>min (0 = disabled)</Text>
					</RowLR>
					<RowLR mt={5} splitAt={splitAt}>
						<FieldLabel text="Pan delay (min):" fieldPath={fp("desync_panDelayMin")}/>
						<Spinner min={0} step={0.5} value={config.desync_panDelayMin} onChange={val=>ChangeConfig(c=>c.desync_panDelayMin = val)}/>
						<Text ml={3}>s</Text>
					</RowLR>
					<RowLR mt={5} splitAt={splitAt}>
						<FieldLabel text="Pan delay (max):" fieldPath={fp("desync_panDelayMax")}/>
						<Spinner min={0} step={0.5} value={config.desync_panDelayMax} onChange={val=>ChangeConfig(c=>c.desync_panDelayMax = val)}/>
						<Text ml={3}>s</Text>
					</RowLR>
				</>}

				{/* Media pools */}
				<Row mt={10} center>
					<Text style={{fontWeight: "bold"}}>Media Pools:</Text>
					{pools.map((pool, i)=>(
						<Button key={i} ml={5} p="2px 5px"
							text={`${i + 1}: ${pool.name || "unnamed"}${!pool.enabled ? " [d]" : ""}`}
							style={selectedPoolIndex === i ? {backgroundColor: "rgba(100,200,100,.5)"} : undefined}
							onClick={()=>setSelectedPoolIndex(i)}/>
					))}
					<Button ml={5} mdIcon="plus" enabled={enabled} onClick={()=>{
						ChangeConfig(c=>{
							if (!c.mediaPools) c.mediaPools = [];
							c.mediaPools.push(new MediaPool());
						});
						setSelectedPoolIndex(pools.length); // select the new pool
					}}/>
				</Row>

				{pools.length > 0 && selectedPoolIndex < pools.length &&
				<MediaPoolUI
					pool={pools[selectedPoolIndex]}
					poolIndex={selectedPoolIndex}
					{...sharedProps}
				/>}
			</>}
		</Column>
	);
});

const MediaPoolUI = observer_mgl((props: {pool: MediaPool, poolIndex: number} & SharedProps_MediaConfigUI)=>{
	const {pool, poolIndex, enabled, config, ChangeConfig} = props;

	const ChangePool = (mutator: (pool: MediaPool)=>void)=>{
		ChangeConfig(c=>{
			const newPool = Clone(pool) as MediaPool;
			mutator(newPool);
			c.mediaPools[poolIndex] = newPool;
		});
	};

	return (
		<Column ml={10} mt={5} style={{borderLeft: "2px solid rgba(255,255,255,.15)", paddingLeft: 8}}>
			<Row center>
				<Text>Name:</Text>
				<TextInput ml={5} enabled={enabled} value={pool.name} onChange={val=>ChangePool(p=>p.name = val)}/>
				<CheckBox ml={10} text="Enabled" enabled={enabled} value={pool.enabled} onChange={val=>ChangePool(p=>p.enabled = val)}/>
				<Button ml="auto" mdIcon="delete" enabled={enabled} onClick={()=>{
					ShowMessageBox({
						title: "Delete pool?", cancelButton: true,
						message: `Permanently delete this pool?`,
						onOK: ()=>{
							ChangeConfig(c=>c.mediaPools.splice(poolIndex, 1));
						},
					});
				}}/>
			</Row>
			<RowLR mt={5} splitAt={splitAt}>
				<TextPlus info="Audio files whose path contains this substring will appear in this pool.">Path substring:</TextPlus>
				<TextInput enabled={enabled} style={{flex: 1}} value={pool.path} onChange={val=>ChangePool(p=>p.path = val)}/>
			</RowLR>
			<RowLR mt={5} splitAt={splitAt}>
				<Text>Volume:</Text>
				<SpinnerForPercent enabled={enabled} value={pool.volume} onChange={val=>ChangePool(p=>p.volume = val)}/>
			</RowLR>
		</Column>
	);
});